import { queryClient } from "App";
import { get } from "lodash";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getNoticesTableColumns,
  getNoticesTableFilters,
} from "routes/campaign/components/notices_table/NoticesTableColumnsAndFilters";
import CampaignService from "services/Campaign";
import EmailTemplateService from "services/EmailTemplate";
import TagService from "services/Tag";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";
import tagConstants from "constants/Tag";
import { formatUsersForFilter } from "helpers/user";
import { noticeKeys } from "routes/campaign/components/notices_table/queryKeys";

const fetchUsers = async () => {
  const rawUsers = await UserService.getUsers();
  return formatUsersForFilter(rawUsers);
};

const fetchTemplates = async () => {
  const rawTemplates = await EmailTemplateService.getEmailTemplatesForFilters();
  const templates = rawTemplates.map(template => ({
    id: template.id,
    name: template.name,
  }));
  return templates;
};

const fetchTags = async className => {
  const taggableModels = tagConstants.taggableModels;
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === className
  );

  const tags = await TagService.getTags({ model: model, for_filtering: true });
  return tags;
};

export const useNoticesTable = ({
  entityId = null,
  campaignId = null,
  className = null,
  tableName = null,
  handleOpenResponsesModal = null,
}) => {
  const { data: users, isSuccess: isUsersFetched } = useQuery({
    queryKey: ["users"],
    queryFn: fetchUsers,
  });
  const { data: templates, isSuccess: isTemplatesFetched } = useQuery({
    queryKey: ["templates"],
    queryFn: fetchTemplates,
  });
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", className],
    queryFn: () => fetchTags(className),
  });
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const queryKey = useMemo(
    () => noticeKeys.list({ campaignId, entityId }),
    [entityId, campaignId]
  );

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const deleteNotices = useCallback(
    async (ids = []) => {
      await CampaignService.deleteCampaignTargetEmails(ids);
      await invalidateData();
    },
    [invalidateData]
  );

  const filters = useMemo(() => {
    if (!isUsersFetched || !isTemplatesFetched || !isTagsFetched) {
      return [];
    }
    return getNoticesTableFilters({
      users,
      templates,
      tags,
      entityId,
    });
  }, [
    users,
    templates,
    tags,
    entityId,
    isUsersFetched,
    isTemplatesFetched,
    isTagsFetched,
  ]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getNoticesTableColumns({
      entityId,
      handleOpenResponsesModal,
      invalidateData,
    });

    const noticesTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: noticesTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    entityId,
    tableName,
    tableSettings,
    isTableSettingsFetched,
    handleOpenResponsesModal,
    invalidateData,
  ]);

  return { filters, columns, tags, queryKey, invalidateData, deleteNotices };
};
